import React, { useEffect, useRef } from 'react';
import { X, Plus, Minus, Trash2, ShoppingBag, MessageCircle, Truck, Lock } from 'lucide-react';
import { CURRENCY_SYMBOL, waGeneral } from '../config/pricing';

export default function CartDrawer({ open, onClose, items, setQty, remove, onCheckout, checkoutState, checkoutError, onShop }) {
  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const itemCount = items.reduce((s, i) => s + i.qty, 0);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    previousFocusRef.current = document.activeElement;
    document.body.style.overflow = 'hidden';
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    const focusTimer = window.setTimeout(() => closeButtonRef.current?.focus(), 120);
    return () => {
      document.removeEventListener('keydown', handler);
      window.clearTimeout(focusTimer);
      requestAnimationFrame(() => {
        if (!document.querySelector('.drawer.open, .product-drawer.open')) {
          document.body.style.overflow = '';
        }
      });
      previousFocusRef.current?.focus?.();
    };
  }, [open, onClose]);

  return (
    <>
      <div className={`drawer-backdrop ${open ? 'open' : ''}`} onClick={onClose} />
      <aside
        className={`drawer ${open ? 'open' : ''}`}
        aria-label="Shopping cart"
        aria-hidden={!open}
        aria-modal={open}
        role="dialog"
      >
        <header className="drawer-head">
          <div>
            <h3 className="serif">Your Atelier Bag</h3>
            {itemCount > 0 && <span className="drawer-count">{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>}
          </div>
          <button ref={closeButtonRef} type="button" className="icon-btn" onClick={onClose} aria-label="Close cart"><X size={18} /></button>
        </header>

        <div className="drawer-body">
          {items.length === 0 ? (
            <div className="drawer-empty">
              <div className="ico"><ShoppingBag size={26} /></div>
              <p className="serif" style={{ fontSize: '1.25rem', color: 'var(--bone)', marginBottom: 6 }}>
                Your bag is empty
              </p>
              <p style={{ fontSize: '.85rem' }}>Discover the collection and find your signature scent.</p>
              {onShop && (
                <button
                  type="button"
                  className="btn btn-gold btn-sm"
                  onClick={() => {
                    onClose();
                    setTimeout(onShop, 80);
                  }}
                >
                  Shop Collection
                </button>
              )}
            </div>
          ) : (
            items.map((it) => (
              <div key={it.key} className="line">
                <div className="line-img" aria-hidden="true">
                  {it.image ? (
                    <img src={it.image} alt="" width="64" height="64" loading="lazy" decoding="async" />
                  ) : (
                    it.name[0]
                  )}
                </div>
                <div>
                  <div className="line-name">{it.name}</div>
                  <div className="line-meta">{it.sizeLabel} · {CURRENCY_SYMBOL}{it.price}</div>
                  <div className="line-actions">
                    <div className="qty">
                      <button type="button" onClick={() => setQty(it.key, Math.max(1, it.qty - 1))} aria-label={`Decrease ${it.name} quantity`}><Minus size={12} /></button>
                      <span>{it.qty}</span>
                      <button type="button" onClick={() => setQty(it.key, it.qty + 1)} aria-label={`Increase ${it.name} quantity`}><Plus size={12} /></button>
                    </div>
                    <button type="button" className="line-remove" onClick={() => remove(it.key)} aria-label={`Remove ${it.name}`}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
                <div className="line-price">{CURRENCY_SYMBOL}{it.price * it.qty}</div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <footer className="drawer-foot">
            <div className="subtotal-row">
              <span>Subtotal</span>
              <span>{CURRENCY_SYMBOL}{subtotal}</span>
            </div>
            <div className="free-ship">
              <Truck size={14} /> Free UK delivery on every order
            </div>
            <p className="checkout-note"><Lock size={12} /> Card details are handled securely by Stripe.</p>
            <button
              className="btn btn-gold btn-block"
              type="button"
              onClick={onCheckout}
              disabled={checkoutState === 'loading'}
            >
              <Lock size={14} />
              {checkoutState === 'loading'
                ? 'Preparing checkout...'
                : 'Secure Card Checkout'}
            </button>
            <div className="cart-or">or</div>
            <a
              className="btn btn-wa btn-block"
              href={waGeneral()}
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle size={14} /> Order on WhatsApp
            </a>
            {checkoutState === 'error' && (
              <p className="checkout-error">
                {checkoutError || 'Card checkout is temporarily unavailable. Please order on WhatsApp.'}
              </p>
            )}
          </footer>
        )}
      </aside>
    </>
  );
}
